/**
 * REST client for the LIVE CLASSES API (F3-G). Wraps the control plane's
 * endpoints (`/api/live/*`). Uses the same authenticated transport as the
 * media API (`mediaApiFetch`: cookie-BFF on web, Bearer injected in Rust on
 * native), so it does NOT depend on the typed openapi client (avoids regenerating it).
 *
 * `meeting_config` is SERVER-ONLY: join only returns what the client is
 * allowed to see (domain+room for embed, or URL for external).
 */
import { mediaApiFetch } from "@/lib/api";

/** Embed data (Jitsi): server domain + deterministic room. */
export interface JoinEmbed {
  domain: string;
  room: string;
}

/** Response of `GET /api/live/sessions/:id/join` when the window is open. */
export interface JoinResponse {
  provider: string;
  mode: "embed" | "external";
  joinUrl?: string;
  embed?: JoinEmbed;
  startsAt: string;
  endsAt: string;
  topic?: string;
  cohortName: string;
}

/** Join result: success, or rejection due to window/cancellation (403 with reason). */
export type JoinResult =
  | { ok: true; data: JoinResponse }
  | { ok: false; reason: string; opensAt: string | null };

/** UNEXPECTED network/server error (distinct from the window 403, which is data). */
class LiveApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "LiveApiError";
  }
}

export type LiveFetch = (path: string, init?: RequestInit) => Promise<Response>;

export interface LiveApi {
  /** Resolves how to join; the window/cancellation 403 is returned as data. */
  join(sessionId: string): Promise<JoinResult>;
  /** Teacher/admin: sets provider + config for ONE session. */
  setMeeting(sessionId: string, provider: string, config: Record<string, unknown>): Promise<void>;
  /** Teacher/admin: provider default at the COHORT level. */
  setCohortDefaults(
    cohortId: string,
    provider: string,
    config: Record<string, unknown>,
  ): Promise<void>;
  /** Teacher/admin: links a recording (video_asset) to the session. */
  attachRecording(sessionId: string, videoAssetId: string): Promise<void>;
}

function enc(s: string): string {
  return encodeURIComponent(s);
}

async function expectOk(res: Response, ctx: string): Promise<void> {
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    // oxlint-disable-next-line lingui/no-unlocalized-strings -- internal transport diagnostics, not UI text
    throw new LiveApiError(`${ctx}: HTTP ${res.status} ${body}`.trim(), res.status);
  }
}

function createLiveApi(fetchImpl: LiveFetch = mediaApiFetch): LiveApi {
  return {
    async join(sessionId) {
      const res = await fetchImpl(`/api/live/sessions/${enc(sessionId)}/join`);
      if (res.status === 403) {
        // Window/cancellation 403: body { reason, opensAt } — this is DATA, not an error.
        // Read at the server's trust boundary; the caller validates what it uses.
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        const body = (await res.json().catch(() => ({}))) as {
          reason?: string;
          opensAt?: string | null;
        };
        return { ok: false, reason: body.reason ?? "forbidden", opensAt: body.opensAt ?? null };
      }
      await expectOk(res, "join");
      // Trust-boundary read; the consumer validates what it uses.
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      return { ok: true, data: (await res.json()) as JoinResponse };
    },

    async setMeeting(sessionId, provider, config) {
      const res = await fetchImpl(`/api/live/sessions/${enc(sessionId)}/meeting`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, config }),
      });
      await expectOk(res, "setMeeting");
    },

    async setCohortDefaults(cohortId, provider, config) {
      const res = await fetchImpl(`/api/live/cohorts/${enc(cohortId)}/meeting-defaults`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, config }),
      });
      await expectOk(res, "setCohortDefaults");
    },

    async attachRecording(sessionId, videoAssetId) {
      const res = await fetchImpl(`/api/live/sessions/${enc(sessionId)}/recording`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ videoAssetId }),
      });
      await expectOk(res, "attachRecording");
    },
  };
}

/** Default instance (real transport). */
export const liveApi: LiveApi = createLiveApi();
